<template>
    <div class="fixed-center full-width">
        <div v-if="!isLoggedIn">
            <div v-if="!showPassRecovery" class="row justify-center">
                <login :autoActivationMode="true"
                       @forgot="showPassRecovery = true"
                       @loggedIn="retrieveActivation" />
            </div>
            <div v-else>
                <forgot-pass :autoActivationMode="true"
                             @back="showPassRecovery = false"
                             @login="showPassRecovery = false" />
            </div>
        </div>
        <div v-else-if="invalid" class="row justify-center">
            <q-card style="max-width: 600px">
                <q-card-section class="bg-primary text-white">
                    <div class="text-h6 row items-center">
                        <q-icon name="mdi-card-text" size="sm" class="q-mr-xs" />{{ errorTitle }}
                    </div>
                </q-card-section>
                <q-card-section v-html="errorText">
                </q-card-section>

            </q-card>
        </div>
        <div v-else-if="Object.keys(this.$route.query).length == 0" class="row justify-center">
            <q-card style="max-width: 600px">
                <q-card-section class="bg-primary text-white">
                    <div class="text-h6 row items-center">
                        <q-icon name="mdi-card-text" size="sm" class="q-mr-xs" />Activation Code Retrieval Service
                    </div>
                </q-card-section>
                <q-card-section>
                    You have selected the option to retrieve your activation code through our online web service. Please
                    follow the instructions below to activate your software.
                </q-card-section>
                <q-card-section>
                    Once you have provided your Install ID and selected the appropriate product from the drop down box, click the "Retrieve Code" button to retrieve your activation code.
                </q-card-section>
                <q-card-section>
                    <q-form @submit="onSubmit" class="q-gutter-md" autocomplete="off">
                        <q-input dense
                                 label="Install ID" stack-label
                                 autocomplete="on" name="installationId"
                                 :loading="isInstallIdValidPending"
                                 error-message="Install ID is invalid"
                                 :error="!isValidId"
                                 @change="checkInstallIdValid()"
                                 v-model.trim="form.installId"
                                 :rules="[ val => $v.form.installId.required || 'Install ID is required' ]"
                                 hide-bottom-space />
                        <q-select dense
                                  label="Product" stack-label
                                  autocomplete="off"
                                  v-model="form.product" :options="products"
                                  option-value="id"
                                  option-label="name"
                                  :rules="[ val => $v.form.product.required || 'Product is required' ]"
                                  :loading="getProductLinesForLegacyActivationRetrievalPending"
                                  hide-bottom-space />
                        <q-select dense
                                  label="OEM" stack-label
                                  autocomplete="off"
                                  v-model="form.oem" :options="oems"
                                  option-value="id"
                                  option-label="name"
                                  :loading="getOemsPending"
                                  hide-bottom-space />
                        <div class="text-center">
                            <q-btn label="Retrieve Code" type="submit" color="primary" />
                        </div>
                    </q-form>
                </q-card-section>
            </q-card>
        </div>
        <div v-else-if="getAutoActivationData" class="row justify-center">
            <q-card style="max-width: 480px">
                <q-card-section>
                    <div>
                        <q-input outlined
                                 readonly
                                 label="Activation Code" stack-label
                                 v-model="getAutoActivationData"
                                 input-class="text-positive text-bold"
                                 hide-bottom-space>
                            <template v-slot:append>
                                <q-btn flat color="primary" icon="mdi-content-copy" dense
                                       @click="copyClipboard(getAutoActivationData)">
                                    <q-tooltip>
                                        Copy to clipboard
                                    </q-tooltip>
                                </q-btn>
                            </template>
                        </q-input>
                        <div class="q-mt-md">
                            For instructions on how to apply your activation code to your software <router-link to="/support/Help/Enter-Activation" target="_blank" style="text-decoration: underline;">click here</router-link> and follow the <b>Manual Input Option</b> Steps.
                        </div>
                    </div>
                </q-card-section>
            </q-card>
        </div>
        <dialog-activate :show.sync="showDialogActivate"
                         :activation.sync="currentActivation"
                         :license.sync="currentLicense"
                         :isAutoActivation="true"
                         :isLegacyActivationRetrieval="true"
                         :installId="$route.query.InstallID"
                         @back="goBack" />
        <dialog-activations :show.sync="showDialogActivations"
                            :license="currentLicense"
                            :isAutoActivation="true"
                            @activate="activateLicense"
                            @replace="replaceActivation" />

        <q-dialog v-model="showDialogMaxActivationsExceeded" persistent>
            <q-card style="max-width: 450px;" class="full-width">
                <q-card-section>
                    <div class="text-h6 row items-center text-negative">
                        <q-icon name="mdi-cancel" size="sm" class="q-mr-xs" />Max Activations Exceeded
                    </div>
                </q-card-section>
                <q-card-section class="q-pt-none">
                    Sorry, you have exceeded the maximum number of replacement activations allowed at this moment. For further assistance please contact support or call 403.247.8566.
                </q-card-section>
                <q-card-section class="q-pt-none">
                    <div>
                        <q-btn label="Close" color="primary" v-close-popup />
                    </div>
                </q-card-section>
            </q-card>
        </q-dialog>

        <q-inner-loading :showing="getAutoActivationPending || getAlternateActivationPending">
            <div>
                <q-spinner color="primary"
                           :thickness="2"
                           size="3em" />
            </div>
        </q-inner-loading>
    </div>
</template>
<script>
    import login from '@/components/Login'
    import forgotPass from '@/components/ForgotPass'
    import { required, requiredIf } from 'vuelidate/lib/validators'
    import { mapState } from 'vuex'
    import dialogActivate from '@/components/dialogs/DialogActivate'
    import dialogActivations from '@/components/dialogs/DialogActivations'

    export default {
        name: 'retrieve-activation-code',
        components: {
            login,
            forgotPass,
            dialogActivate,
            dialogActivations,
        },
        data() {
            this.baseUrl = process.env.VUE_APP_BASEURL
            return {
                currentLicense: null,
                currentActivation: null,
                showDialogActivate: false,
                showDialogActivations: false,
                showDialogMaxActivationsExceeded: false,
                invalid: false,
                showPassRecovery: false,
                errorTitle: 'No Valid License Was Found',
                errorText: 'We could not find a valid license from your account to activate, please purchase a valid license. For further assistance please contact support or call 403.247.8566.',
                isValidId: true,
                oems: [],
                products: [],
                form: {
                    installId: null,
                    oem: null,
                    product: null,
                },
            }
        },
        validations: {
            form: {
                installId: { required },
                product: { required },
            }
        },
        methods: {
            goBack() {
                this.currentActivation = null
                this.showDialogActivate = false
                this.showDialogActivations = true
            },
            replaceActivation(activation) {
                this.currentActivation = activation
                this.$store.dispatch('getLicense', this.currentActivation.licenseID).then(() => {
                    this.currentLicense = this.getLicenseData
                    this.$store.dispatch('isMaxActivationsExceeded', this.currentLicense).then(() => {
                        if (this.isMaxActivationsExceededData)
                            this.showDialogMaxActivationsExceeded = true
                        else
                            this.showDialogActivate = true
                    })
                })
            },
            activateLicense(license) {
                this.currentLicense = license
                this.currentActivation = null
                this.showDialogActivate = true
            },
            redirectUrl(url) {
                if (url)
                    window.location.replace(this.baseUrl + 'retrieveactivationcode' + url)
            },
            checkInstallIdValid() {
                if (this.form.installId) {
                    this.$store.dispatch('validateInstallId', this.form).then(() => {
                        if (typeof this.isInstallIdValidData !== 'undefined') {
                            this.isValidId = this.isInstallIdValidData
                        }
                    })
                }
            },
            getOems() {
                this.oems = []
                this.$store.dispatch('getOEMs').then(() => {
                    this.oems = this.getOemsData
                    if (this.oems) {
                        // Add the blank option to the oems array
                        this.oems.push({ id: null, name: '' })
                    }
                    this.form.oem = this.oems?.find(o => o.id == null) ?? null
                })
            },
            getProducts() {
                this.products = []
                this.$store.dispatch('getProductLinesForLegacyActivationRetrieval').then(() => {
                    this.products = this.getProductLinesForLegacyActivationRetrievalData
                })
            },
            onSubmit() {
                this.$v.form.$touch()
                if (this.$v.form.$pending || this.$v.form.$error) return

                let url = `?ProductLineID=${this.form.product.id}&InstallID=${this.form.installId}&OEMID=${this.form.oem.id ?? ''}`
                this.redirectUrl(url)
            },
            retrieveActivation() {
                this.$store.dispatch('getActivationCode', this.$route.query).then(() => {
                    if (this.getAutoActivationData) {
                        return
                    }

                    // No activation code found, attempt alternative activation route
                    this.$store.dispatch('getAlternateActivation', this.$route.query).then(() => {
                        if (!this.getAlternateActivationData?.license) {
                            // Activation code not found
                            this.$store.dispatch('getActivationErrorInstructions', this.getAlternateActivationData?.status).then(() => {
                                if (this.getActivationErrorInstructionsData) {
                                    this.errorTitle = this.getActivationErrorInstructionsData.item1
                                    this.errorText = this.getActivationErrorInstructionsData.item2
                                }
                                this.invalid = true
                            })
                            return
                        }

                        if (this.getAlternateActivationData?.status && this.getAlternateActivationData?.license) {
                            this.currentLicense = this.getAlternateActivationData.license

                            if (this.getAlternateActivationData.status == 'UpdateVersion') {
                                let activation = this.currentLicense.activations.find(a => a.installID == this.$route.query?.InstallID)
                                return
                            }
                            if (this.getAlternateActivationData.status == 'Activate') {
                                if (this.currentLicense.activations.filter(a => a.isCurrent).length == 0)
                                    this.showDialogActivate = true
                                else
                                    this.showDialogActivations = true
                                return
                            }
                        }
                        return
                    })
                })
            },
        },
        computed: {
            ...mapState([
                'infoFromSoftware',

                'getAutoActivationData',
                'getAutoActivationPending',

                'getAlternateActivationData',
                'getAlternateActivationPending',

                'getLicenseData',
                'getLicensePending',

                'isInstallIdValidPending',
                'isInstallIdValidData',

                'getOemsPending',
                'getOemsData',

                'getProductLinesForLegacyActivationRetrievalPending',
                'getProductLinesForLegacyActivationRetrievalData',

                'getActivationErrorInstructionsPending',
                'getActivationErrorInstructionsData',
            ]),
            screenSize() {
                return this.$q.screen
            },
            isLoggedIn() { return this.$store.getters.isLoggedIn },
        },
        created() {
            if (this.$route.query) {
                this.$store.commit('update_inforFromSoftware', this.$route.query)
            }

            if (this.$route.query?.PageType == 'Invalid') {
                this.invalid = true
                return
            }

            if (this.$route.query?.ActivationProcessed) {
                this.invalid = false
                return
            }

            this.showPassRecovery = false
            if (this.isLoggedIn && Object.keys(this.$route.query).length > 0) this.retrieveActivation()

            if (Object.keys(this.$route.query).length == 0) {
                this.getOems()
                this.getProducts()
            }
        },
        watch: {
            isLoggedIn(newVal) {
                if (newVal && Object.keys(this.$route.query).length > 0) {
                    this.retrieveActivation()
                }
            },
        }

    }
</script>
